import { Preferences } from "@capacitor/preferences";

// Reconnection tokens for the live matches this device is seated in, keyed by
// the match's gameId (the one identifier a match goes by everywhere). Which
// matches are actually still reconnectable is the server's call — these
// tokens are only the credential colyseus.js needs to re-enter a held seat,
// and each one is minted per room connection. Written while a match is under
// way (setup/playing) and kept fresh per connection; entries are removed when
// a match ends, a reconnect attempt fails, or the server stops listing it.
// Same Capacitor Preferences backing as the auth tokens (localStorage on web,
// native prefs on mobile).

const ACTIVE_GAMES_KEY = "fortress.activeGames";

/** gameId → colyseus.js reconnection token (minted fresh per connection). */
export type ActiveGameTokens = Record<string, string>;

export async function getActiveGameTokens(): Promise<ActiveGameTokens> {
  const { value } = await Preferences.get({ key: ACTIVE_GAMES_KEY });
  if (!value) return {};
  try {
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const out: ActiveGameTokens = {};
    for (const [gameId, token] of Object.entries(parsed)) {
      if (typeof token === "string") out[gameId] = token;
    }
    return out;
  } catch {
    return {};
  }
}

async function writeTokens(tokens: ActiveGameTokens): Promise<void> {
  if (Object.keys(tokens).length === 0) {
    await Preferences.remove({ key: ACTIVE_GAMES_KEY });
    return;
  }
  await Preferences.set({
    key: ACTIVE_GAMES_KEY,
    value: JSON.stringify(tokens),
  });
}

export async function setActiveGameToken(
  gameId: string,
  reconnectionToken: string,
): Promise<void> {
  const tokens = await getActiveGameTokens();
  tokens[gameId] = reconnectionToken;
  await writeTokens(tokens);
}

export async function removeActiveGameToken(gameId: string): Promise<void> {
  const tokens = await getActiveGameTokens();
  if (!(gameId in tokens)) return;
  delete tokens[gameId];
  await writeTokens(tokens);
}
